import React, { useState, useRef, useEffect } from 'react';
import { K } from '../constants';
import { collection, onSnapshot, query, orderBy, addDoc, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { useToast } from '../components/Toast';
import { Plus, X } from 'lucide-react';

const BIG = { fontFamily: K.fSans, fontWeight: 700, letterSpacing: 0.3, lineHeight: 1 };

/* Default feeds — empty, admins add via Firestore */
const DEFAULT_FEEDS = [];

const CATEGORIES = [
  { id: 'local', label: 'Local' },
  { id: 'mutual_aid', label: 'Mutual Aid' },
  { id: 'statewide', label: 'Statewide' },
  { id: 'other', label: 'Other' },
];

function useRadioFeeds() {
  const [feeds, setFeeds] = useState(DEFAULT_FEEDS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(collection(db, 'radioFeeds'), orderBy('order', 'asc'));
    return onSnapshot(q, (snap) => {
      if (snap.empty) {
        setFeeds(DEFAULT_FEEDS);
      } else {
        setFeeds(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      }
      setLoading(false);
    }, () => setLoading(false));
  }, []);

  return { feeds, loading };
}

function AddFeedForm({ onClose }) {
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('local');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!name.trim() || !url.trim()) return;
    setSaving(true);
    try {
      await addDoc(collection(db, 'radioFeeds'), {
        name: name.trim(),
        url: url.trim(),
        description: description.trim(),
        category,
        order: Date.now(),
        createdAt: new Date(),
      });
      onClose();
    } catch (e) {
      console.error('Failed to add feed:', e);
      setSaving(false);
    }
  };

  const inpSt = {
    background: K.bg2, border: `1px solid ${K.bdr}`, borderRadius: 8, color: K.t0,
    fontFamily: K.fSans, fontSize: 13, padding: '10px 12px',
    width: '100%', boxSizing: 'border-box', outline: 'none',
  };

  return (
    <div style={{ background: K.bg1, border: `1px solid ${K.bdr}`, borderRadius: 12, padding: 14, marginBottom: 12, boxShadow: K.sh1 }}>
      <div style={{ fontFamily: K.fSans, fontSize: 12, fontWeight: 600, color: K.t2, letterSpacing: 0.3, marginBottom: 10 }}>Add feed</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Feed name" style={inpSt} />
        <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="Stream URL (MP3/AAC)" style={inpSt} />
        <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Description (optional)" style={inpSt} />
        <select value={category} onChange={(e) => setCategory(e.target.value)} style={{ ...inpSt, fontFamily: 'inherit' }}>
          {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
          <button onClick={handleSave} disabled={saving || !name.trim() || !url.trim()} style={{
            flex: 1, background: K.teal, color: '#fff', border: 'none', borderRadius: 8, padding: '10px 0',
            cursor: 'pointer', fontFamily: K.fSans, fontSize: 12, fontWeight: 600,
            letterSpacing: 0.3, opacity: saving ? 0.5 : 1,
          }}>{saving ? 'Saving...' : 'Add feed'}</button>
          <button onClick={onClose} style={{
            background: K.bg2, color: K.t2, border: `1px solid ${K.bdr}`, borderRadius: 8, padding: '10px 16px',
            cursor: 'pointer', fontSize: 12, fontWeight: 600,
          }}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

export function Radio({ isAdmin }) {
  const { feeds, loading } = useRadioFeeds();
  const toast = useToast();
  const [playing, setPlaying] = useState(null); // feed id
  const [showAdd, setShowAdd] = useState(false);
  const audioRef = useRef(null);

  const handlePlay = (feed) => {
    if (!feed.url) return;
    if (playing === feed.id) {
      // Stop
      if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
      setPlaying(null);
    } else {
      // Play new
      if (audioRef.current) { audioRef.current.pause(); }
      const audio = new Audio(feed.url);
      audio.play().catch((err) => {
        console.warn('[Radio] audio play failed:', err);
        toast(`Couldn't play "${feed.name || 'feed'}" — stream may be offline`, 'error');
        setPlaying(null);
      });
      audio.onerror = () => {
        toast(`"${feed.name || 'Feed'}" stream error`, 'error');
        setPlaying(null);
      };
      audio.onended = () => setPlaying(null);
      audioRef.current = audio;
      setPlaying(feed.id);
    }
  };

  const handleDelete = async (feedId) => {
    if (feedId.startsWith('_d')) return; // don't delete defaults
    try {
      await deleteDoc(doc(db, 'radioFeeds', feedId));
      if (playing === feedId) {
        if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
        setPlaying(null);
      }
    } catch (e) {
      console.error('Failed to delete feed:', e);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => { if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; } };
  }, []);

  const playingFeed = feeds.find((f) => f.id === playing);
  const grouped = CATEGORIES.map((cat) => ({
    ...cat,
    feeds: feeds.filter((f) => f.category === cat.id),
  })).filter((g) => g.feeds.length > 0);

  if (loading) return <div style={{ textAlign: 'center', padding: 40, color: K.t3 }}>Loading feeds...</div>;

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <div style={{ ...BIG, fontSize: 28, color: K.teal }}>Radio</div>
        <div style={{ fontFamily: K.fSans, fontSize: 12, fontWeight: 500, color: K.t2, letterSpacing: 0.3, marginTop: 3 }}>Scanner feeds</div>
      </div>

      {/* Now Playing bar */}
      {playingFeed && (
        <div style={{
          background: `${K.teal}15`, border: `1px solid ${K.teal}44`, borderRadius: 10,
          padding: '10px 14px', marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 10, height: 10, borderRadius: '50%', background: K.teal, boxShadow: `0 0 8px ${K.teal}`, animation: 'pulse 1.5s infinite' }} />
            <div>
              <div style={{ fontFamily: K.fSans, fontSize: 13, fontWeight: 600, color: K.teal }}>{playingFeed.name}</div>
              <div style={{ fontSize: 10, color: K.t3 }}>Live</div>
            </div>
          </div>
          <button onClick={() => handlePlay(playingFeed)} style={{
            background: K.bg2, border: `1px solid ${K.bdr}`, borderRadius: 8, padding: '5px 12px',
            cursor: 'pointer', fontFamily: K.fSans, fontSize: 11, fontWeight: 600,
            color: K.bad, letterSpacing: 0.3,
          }}>Stop</button>
        </div>
      )}

      {/* Admin add button */}
      {isAdmin && !showAdd && (
        <button onClick={() => setShowAdd(true)} style={{
          width: '100%', background: K.bg1, border: `1px dashed ${K.teal}44`, borderRadius: 9,
          padding: '10px 0', cursor: 'pointer', fontFamily: K.fSans, fontSize: 12,
          fontWeight: 600, color: K.teal, letterSpacing: 0.3, marginBottom: 12,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
        }}><Plus size={16} color={K.teal} strokeWidth={1.75} />Add feed</button>
      )}
      {isAdmin && showAdd && <AddFeedForm onClose={() => setShowAdd(false)} />}

      {/* Feed list by category */}
      {grouped.map((group) => (
        <div key={group.id} style={{ marginBottom: 16 }}>
          <div style={{ fontFamily: K.fSans, fontSize: 11, fontWeight: 600, color: K.t3, letterSpacing: 0.3, marginBottom: 8 }}>{group.label}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {group.feeds.map((feed) => {
              const isPlaying = playing === feed.id;
              const hasUrl = !!feed.url;
              return (
                <div key={feed.id} style={{
                  background: isPlaying ? `${K.teal}12` : K.bg1,
                  border: `1px solid ${isPlaying ? K.teal + '44' : K.bdr}`,
                  borderRadius: 10, padding: '12px 14px',
                  display: 'flex', alignItems: 'center', gap: 12,
                }}>
                  {/* Play/Stop button */}
                  <button onClick={() => handlePlay(feed)} disabled={!hasUrl} style={{
                    width: 38, height: 38, borderRadius: '50%',
                    background: isPlaying ? K.teal : hasUrl ? K.bg3 : K.bg2,
                    border: `1px solid ${isPlaying ? K.teal : hasUrl ? K.bdr : K.bdr}`,
                    cursor: hasUrl ? 'pointer' : 'default',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0, opacity: hasUrl ? 1 : 0.4,
                  }}>
                    {isPlaying ? (
                      <div style={{ width: 12, height: 12, borderRadius: 2, background: '#fff' }} />
                    ) : (
                      <div style={{ width: 0, height: 0, borderStyle: 'solid', borderWidth: '7px 0 7px 12px', borderColor: `transparent transparent transparent ${hasUrl ? K.t1 : K.t3}`, marginLeft: 2 }} />
                    )}
                  </button>
                  {/* Feed info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontFamily: K.fSans, fontSize: 14, fontWeight: 600, color: isPlaying ? K.teal : K.t0, letterSpacing: 0.3 }}>
                      {feed.name}
                    </div>
                    {feed.description && (
                      <div style={{ fontSize: 11, color: K.t3, marginTop: 2 }}>{feed.description}</div>
                    )}
                    {!hasUrl && (
                      <div style={{ fontSize: 10, color: K.warn, marginTop: 2 }}>No stream URL configured</div>
                    )}
                  </div>
                  {/* Admin delete */}
                  {isAdmin && !feed.id.startsWith('_d') && (
                    <button onClick={() => handleDelete(feed.id)} style={{
                      background: 'none', border: 'none', color: K.t3, cursor: 'pointer',
                      padding: '4px 8px', opacity: 0.5, display: 'flex', alignItems: 'center',
                    }}><X size={16} color={K.t3} strokeWidth={1.75} /></button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {feeds.length === 0 && (
        <div style={{ textAlign: 'center', padding: 40, color: K.t3 }}>
          No feeds configured. {isAdmin ? 'Tap "+ Add Feed" to get started.' : 'Ask an admin to add scanner feeds.'}
        </div>
      )}
    </div>
  );
}

/* Floating mini player — shown globally when a feed is playing */
export function RadioMiniPlayer({ playing, feedName, onStop, onOpen }) {
  if (!playing) return null;
  return (
    <button onClick={onOpen} style={{
      position: 'fixed', bottom: 62, right: 12, zIndex: 500,
      background: K.bg1, border: `1px solid ${K.teal}`, borderRadius: 12,
      padding: '8px 14px 8px 10px', cursor: 'pointer',
      display: 'flex', alignItems: 'center', gap: 8,
      boxShadow: `0 4px 16px rgba(0,0,0,0.4), 0 0 12px ${K.teal}22`,
    }}>
      <div style={{ width: 8, height: 8, borderRadius: '50%', background: K.teal, boxShadow: `0 0 6px ${K.teal}`, animation: 'pulse 1.5s infinite', flexShrink: 0 }} />
      <div style={{ fontFamily: K.fSans, fontSize: 11, fontWeight: 600, color: K.teal, letterSpacing: 0.3, whiteSpace: 'nowrap', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {feedName}
      </div>
      <div onClick={(e) => { e.stopPropagation(); onStop(); }} style={{
        background: `${K.bad}22`, borderRadius: 6, padding: '2px 6px',
        fontFamily: K.fSans, fontSize: 9, fontWeight: 600, color: K.bad, letterSpacing: 0.3,
      }}>Stop</div>
    </button>
  );
}

/* Floating radio button — opens radio tab */
export function RadioFAB({ onClick }) {
  return (
    <button onClick={onClick} style={{
      position: 'fixed', bottom: 112, right: 12, zIndex: 400,
      width: 44, height: 44, borderRadius: '50%',
      background: K.bg1, border: `1px solid ${K.teal}`,
      cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
      boxShadow: `0 4px 16px rgba(0,0,0,0.3)`,
    }}>
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={K.teal} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4.9 19.1C1 15.2 1 8.8 4.9 4.9" />
        <path d="M7.8 16.2c-2.3-2.3-2.3-6.1 0-8.4" />
        <path d="M16.2 7.8c2.3 2.3 2.3 6.1 0 8.4" />
        <path d="M19.1 4.9C23 8.8 23 15.2 19.1 19.1" />
        <circle cx="12" cy="12" r="2" fill={K.teal} />
      </svg>
    </button>
  );
}
